import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Paper,
  TextField,
  Typography,
} from '@material-ui/core';
import { Close as CloseIcon } from '@material-ui/icons';
import CheckIcon from '@material-ui/icons/Check';
import { ReactComponent as Arrow } from '@orientaction/Asset/svg/arrow-up.svg';
import { ReactComponent as FileDownloadIcon } from '@orientaction/Asset/svg/download.svg';
import { ReactComponent as EmailIcon } from '@orientaction/Asset/svg/mail.svg';
import RichEditor from '@orientaction/commons/RichEditor';
import { DrawerRight, Layout } from '@orientaction/components';
import EditorQuill from '@orientaction/components/EditorQuill';
import { aiService } from '@orientaction/services';
import { CONSULTATION_TYPE_CONFIG, formatChatTimestamp, formatDate } from '@orientaction/utils';
import { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useHistory, useParams } from 'react-router-dom';
import { useSnackbar } from 'react-simple-snackbar';
import remarkGfm from 'remark-gfm';
import { ConsultationDetails, Message, ProcessingStatus } from 'src/services/ai.service';
import { useStyles } from './style';

// Polling interval in milliseconds
const POLLING_INTERVAL = 3000;

interface ConsultationPageParams {
  id: string;
  consultationId: string;
}

// Convert Markdown to HTML for RichEditor
const markdownToHtml = (markdown: string): string => {
  if (!markdown) return '';

  const html = markdown
    // Bold
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    // Italic
    .replace(/\*([^*]+)\*/g, '<em>$1</em>')
    // Headings
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    .replace(/^## (.+)$/gm, '<h2>$1</h2>')
    .replace(/^# (.+)$/gm, '<h1>$1</h1>')
    // Line breaks
    .replace(/\n/g, '<br/>');

  return html;
};

const ConsultationPage = () => {
  const classes = useStyles();
  const history = useHistory();
  const { id: beneficiaryId, consultationId } = useParams<ConsultationPageParams>();
  const [openSnackbar] = useSnackbar({
    position: 'bottom-right',
    style: {
      position: 'fixed',
      backgroundColor: '#0E1247',
      color: '#FFFFFF',
      fontFamily: 'Poppins, sans-serif',
      fontSize: '16px',
      fontWeight: 400,
      borderRadius: '8px',
      padding: '8px 8px',
      minWidth: '400px',
      minHeight: '48px',
      boxShadow: '0px 4px 12px rgba(14, 18, 71, 0.3)',
      display: 'flex',
      alignItems: 'center',
      bottom: '24px',
      right: '24px',
      left: 'auto',
      transform: 'none',
      margin: 0,
    },
  });
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);

  const [userInput, setUserInput] = useState('');
  const [notesContent, setNotesContent] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [consultationDetails, setConsultationDetails] = useState<ConsultationDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [isValidatingSynthesis, setIsValidatingSynthesis] = useState(false);
  const [selectedQueries, setSelectedQueries] = useState<string[]>([]);
  const [isCreatingSynthesis, setIsCreatingSynthesis] = useState(false);
  const [showTopGradient, setShowTopGradient] = useState(false);
  const [processingStatus, setProcessingStatus] = useState<ProcessingStatus | null>(null);
  // Ref for chat messages container to enable auto-scrolling
  const chatMessagesRef = useRef<HTMLDivElement>(null);
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Function to scroll to bottom of chat
  const scrollToBottom = () => {
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
    }
  };

  // Fetch functions defined at component level so they can be reused
  const fetchConsultationDetails = async () => {
    try {
      const fetchedConsultationDetails = await aiService.getConsultationDetails(consultationId);
      setConsultationDetails(fetchedConsultationDetails);
      setProcessingStatus(fetchedConsultationDetails.processingStatus);
      // Convert markdown to HTML for RichEditor
      const htmlContent = markdownToHtml(fetchedConsultationDetails.notes || '');
      setNotesContent(htmlContent);
      return fetchedConsultationDetails.processingStatus;
    } catch (error) {
      console.error('Error fetching consultation details:', error);
      return null;
    }
  };

  // Poll for consultation status while processing
  const startPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    pollingIntervalRef.current = setInterval(async () => {
      const status = await fetchConsultationDetails();

      if (status === 'completed' || status === 'failed') {
        // Stop polling when processing is complete or failed
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }

        // Fetch messages if completed successfully
        if (status === 'completed') {
          await fetchMessages();
          setTimeout(() => scrollToBottom(), 100);
        }
      }
    }, POLLING_INTERVAL);
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  const fetchMessages = async () => {
    try {
      const fetchedMessages = await aiService.getConsultationMessages(consultationId);
      setSelectedQueries(fetchedMessages.map(message => message.queryType || ''));
      setMessages(fetchedMessages);
    } catch (error) {
      console.error('Error fetching consultation messages:', error);
      openSnackbar('Erreur lors du chargement des messages');
      // Set empty array if error
      setMessages([]);
    }
  };

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const status = await fetchConsultationDetails();

      // Only fetch messages if processing is completed
      if (status === 'completed') {
        await fetchMessages();
      } else if (status === 'pending' || status === 'processing') {
        // Start polling if still processing
        startPolling();
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
      // Scroll to bottom after data is loaded and rendered
      setTimeout(() => scrollToBottom(), 100);
    }
  };

  // Fetch messages from backend on component mount
  useEffect(() => {
    if (consultationId) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consultationId]);

  // Handle scroll to show/hide top gradient
  useEffect(() => {
    const chatContainer = chatMessagesRef.current;
    if (!chatContainer) return;

    const handleScroll = () => {
      // Show gradient if scrolled down (scrollTop > 0) and there's more content to scroll
      const hasScrollableContent = chatContainer.scrollHeight > chatContainer.clientHeight;
      setShowTopGradient(chatContainer.scrollTop > 0 && hasScrollableContent);
    };

    chatContainer.addEventListener('scroll', handleScroll);
    // Check initial state
    handleScroll();

    return () => {
      chatContainer.removeEventListener('scroll', handleScroll);
    };
  }, [messages, isLoading]);

  const handleCloseAssistant = () => {
    history.push(`/admin/assistant-ia/${beneficiaryId}`);
  };

  const handleSendMessage = async () => {
    if (!userInput.trim() || isSending) return;

    const messageContent = userInput.trim();

    // Add user message to chat immediately for better UX
    const newUserMessage: Message = {
      role: 'user',
      content: messageContent,
      createdAt: new Date().toISOString(),
    };

    setMessages(prev => [...prev, newUserMessage]);
    setUserInput('');
    setIsSending(true);

    try {
      // Send message to backend and get bot response
      const botResponse = await aiService.sendConsultationMessage(consultationId, messageContent);
      fetchData();
    } catch (error) {
      console.error('Error sending message:', error);
      openSnackbar("Erreur lors de l'envoi du message");

      // Optionally remove the user message if sending failed
      // setMessages(prev => prev.slice(0, -1));
    } finally {
      setIsSending(false);
    }
  };

  const handleSynthesis = async () => {
    try {
      setIsCreatingSynthesis(true);
      const botResponse = await aiService.createSynthesis(consultationId, notesContent);
      const fetchedConsultationDetails = await aiService.getConsultationDetails(consultationId);
      setConsultationDetails(fetchedConsultationDetails)
      const htmlContent = markdownToHtml(fetchedConsultationDetails.notes || '');
      setNotesContent(htmlContent);
    } catch (error) {
      console.error('Error creating synthesis:', error);
      openSnackbar('Erreur lors de la synthèse');
    } finally {
      setIsCreatingSynthesis(false);
    }
  };

  const handleValidateSynthesis = async () => {
    try {
      setIsValidatingSynthesis(true);
      await aiService.validateSynthesis(consultationId, notesContent);
      const fetchedConsultationDetails = await aiService.getConsultationDetails(consultationId);
      setConsultationDetails(fetchedConsultationDetails)
    } catch (error) {
      console.error('Error validating synthesis:', error);
      openSnackbar('Erreur lors de la validation de la synthèse');
    } finally {
      setIsValidatingSynthesis(false);
    }
  };
  const handleSaveNotes = async () => {
    try {
      const botResponse = await aiService.saveConsultationNotes(consultationId, notesContent);
      const fetchedConsultationDetails = await aiService.getConsultationDetails(consultationId);
      setConsultationDetails(fetchedConsultationDetails)
    } catch (error) {
      console.error('Error saving notes:', error);
      openSnackbar("Erreur lors de l'enregistrement des notes");
    } 
  };

  const handleExploreScenarios = async (messageContent: string) => {
    try {
      setIsSending(true);
      const botResponse = await aiService.sendConsultationMessage(
        consultationId,
        messageContent,
        true
      );
      fetchData();
    } catch (error) {
      console.error('Error sending message:', error);
      openSnackbar("Erreur lors de l'envoi du message");
    } finally {
      setIsSending(false);
    }
  };

  const getQueryLabel = (queryType: string) => {
    return (
      CONSULTATION_TYPE_CONFIG[
        consultationDetails?.consultationType as keyof typeof CONSULTATION_TYPE_CONFIG
      ]?.queries.find(query => query.value === queryType)?.label || queryType
    );
  };

  const renderRemainingQueries = () => {
    return CONSULTATION_TYPE_CONFIG[
      consultationDetails?.consultationType as keyof typeof CONSULTATION_TYPE_CONFIG
    ]?.queries
      .filter(query => !selectedQueries.includes(query.value))
      .map(query => (
        <Button
          className={classes.exploreButton}
          onClick={() => handleExploreScenarios(query.value)}>
          {getQueryLabel(query.value)}
        </Button>
      ));
  };

  const handleDownloadSynthesis = async () => {
    try {
      await aiService.downloadSynthesis(consultationId);
      openSnackbar(
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
            <circle cx="18" cy="18" r="18" fill="#23296F" />
            <path
              d="M24 14L16 22L12 18"
              stroke="#FFFFFF"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span>La synthèse a été téléchargée avec succès</span>
        </div>
      );
    } catch (error) {
      console.error('Error downloading synthesis:', error);
      openSnackbar('Erreur lors du téléchargement de la synthèse');
    }
  };

  const handleSendEmail = async () => {
    try {
      // Convert newlines to HTML <br> tags
      const normalizedEmailBody = emailBody.replace(/\r?\n/g, '<br>');
      const response = await aiService.sendSynthesisByEmail(
        consultationId,
        emailSubject,
        normalizedEmailBody
      );
      openSnackbar(
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
            <circle cx="18" cy="18" r="18" fill="#23296F" />
            <path
              d="M24 14L16 22L12 18"
              stroke="#FFFFFF"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span>L&apos;email a été envoyé avec succès</span>
        </div>
      );
      setOpenDrawer(false);
    } catch (error) {
      console.error('Error sending email:', error);
      openSnackbar("Erreur lors de l'envoi de l'email");
    }
  };

  const openEmailDrawer = () => {
    const firstName =
      consultationDetails?.beneficiaryInfo?.firstName ||
      consultationDetails?.beneficiaryInfo?.firstname ||
      '';

    setEmailSubject('Synthèse de la consultation - E-Orientaction');

    setEmailBody(
      `Bonjour ${firstName},\n\nVous trouverez ci-joint la synthèse de la consultation effectuée avec votre consultant.`
    );

    setOpenDrawer(true);
  };

  return (
    <Layout>
      <Box className={classes.container}>
        {/* Header with close button */}
        <Box className={classes.header}>
          <Box />
          <Button
            className={classes.closeButton}
            onClick={handleCloseAssistant}
            startIcon={<CloseIcon />}>
            Fermer l&apos;assistant IA
          </Button>
        </Box>

        {/* Main content area */}
        <Grid container={true} className={classes.mainContent} spacing={2}>
          {/* Left side - Chat area */}
          <Grid item={true} xs={7} xl={8}>
            <Box className={classes.chatArea}>
              {/* Chat messages - top area */}
              <Box className={classes.chatMessages} style={{ overflow: 'hidden', position: 'relative' }}>
                {showTopGradient && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 100,
                      background:
                        'linear-gradient(#fff, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0))',
                      pointerEvents: 'none',
                      zIndex: 1,
                    }}
                  />
                )}

                <div ref={chatMessagesRef} style={{ height: '100%', overflowY: 'auto' }}>
                  {isLoading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                      <CircularProgress />
                    </Box>
                  ) : processingStatus === 'pending' || processingStatus === 'processing' ? (
                    <Box
                      display="flex"
                      flexDirection="column"
                      justifyContent="center"
                      alignItems="center"
                      height="100%"
                      style={{ gap: '24px', padding: '40px' }}
                    >
                      <CircularProgress size={48} style={{ color: '#5763FF' }} />
                      <Typography
                        style={{
                          fontSize: '18px',
                          fontWeight: 600,
                          color: '#0E1247',
                          textAlign: 'center'
                        }}
                      >
                        L&apos;assistant IA analyse les données du bénéficiaire
                      </Typography>
                      <Typography
                        style={{
                          fontSize: '14px',
                          color: '#9799B1',
                          textAlign: 'center',
                          maxWidth: '400px'
                        }}
                      >
                        Veuillez patienter, cela peut prendre jusqu&apos;à une minute. Vous pouvez rester sur cette page, elle se mettra à jour automatiquement.
                      </Typography>
                    </Box>
                  ) : processingStatus === 'failed' ? (
                    <Box
                      display="flex"
                      flexDirection="column"
                      justifyContent="center"
                      alignItems="center"
                      height="100%"
                      style={{ gap: '24px', padding: '40px' }}
                    >
                      <Box
                        style={{
                          width: 64,
                          height: 64,
                          borderRadius: '50%',
                          backgroundColor: '#FEE2E2',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <CloseIcon style={{ fontSize: 32, color: '#EF4444' }} />
                      </Box>
                      <Typography
                        style={{
                          fontSize: '18px',
                          fontWeight: 600,
                          color: '#0E1247',
                          textAlign: 'center'
                        }}
                      >
                        Une erreur est survenue
                      </Typography>
                      <Typography
                        style={{
                          fontSize: '14px',
                          color: '#9799B1',
                          textAlign: 'center',
                          maxWidth: '400px'
                        }}
                      >
                        L&apos;analyse n&apos;a pas pu être complétée. Veuillez réessayer en créant une nouvelle consultation.
                      </Typography>
                      <Button
                        className={classes.closeButton}
                        onClick={handleCloseAssistant}
                        style={{ marginTop: '16px' }}
                      >
                        Retour aux consultations
                      </Button>
                    </Box>
                  ) : messages.length === 0 ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                      <Typography color="textSecondary">Aucun message pour le moment</Typography>
                    </Box>
                  ) : (
                    <>
                      {messages.map((message, index) => (
                        <Box
                          key={
                            message.createdAt ? `${message.createdAt}-${index}` : `message-${index}`
                          }
                          className={
                            message.role === 'user'
                              ? classes.messageUserContainer
                              : classes.messageBotContainer
                          }>
                          <div
                            className={
                              message.role === 'user'
                                ? classes.messageUserHeader
                                : classes.messageBotHeader
                            }>
                            <Typography style={{ fontWeight: 500, color: '#0E1247' }}>
                              {message.role === 'user' ? 'Vous' : 'Votre assistant IA'}{' '}
                            </Typography>
                            <Typography style={{ color: '#9799B1', margin: '0 8px' }}>•</Typography>
                            <Typography style={{ color: '#9799B1' }}>
                              {message.createdAt ? formatChatTimestamp(message.createdAt) : ''}{' '}
                            </Typography>
                          </div>
                          <div
                            className={
                              message.role === 'user'
                                ? classes.messageUserContent
                                : classes.messageBotContent
                            }>
                            {message.queryType ? (
                              <Typography>{getQueryLabel(message.queryType)}</Typography>
                            ) : (
                              <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                className="prose max-w-none"
                                components={{
                                  table: ({ node, ...props }) => (
                                    <table className={classes.table} {...(props as any)} />
                                  ),
                                  th: ({ node, ...props }) => (
                                    <th className={classes.tableHeader} {...(props as any)} />
                                  ),
                                  td: ({ node, ...props }) => (
                                    <td className={classes.tableCell} {...(props as any)} />
                                  ),
                                  tr: ({ node, ...props }) => <tr {...(props as any)} />,
                                }}>
                                {message.content}
                              </ReactMarkdown>
                            )}
                          </div>
                        </Box>
                      ))}

                      {isSending && (
                        <Box
                          className={classes.messageBotContainer}
                          style={{ display: 'flex', alignItems: 'center' }}>
                          <CircularProgress size={20} />
                          <Typography style={{ marginLeft: 8 }}>
                            Veuillez patienter environ 20 secondes, l&apos;assistant IA est en cours de réfléxion
                          </Typography>
                        </Box>
                      )}
                    </>
                  )}

                  {!isLoading && !isSending && processingStatus === 'completed' &&
                    CONSULTATION_TYPE_CONFIG[
                      consultationDetails?.consultationType as keyof typeof CONSULTATION_TYPE_CONFIG
                    ]?.queries.filter(query => !selectedQueries.includes(query.value)).length >
                    0 && (
                      <Box style={{ marginTop: 'auto', paddingTop: 16 }}>
                        <Typography className={classes.userLabel}>
                          Avez-vous une autre demande ?
                        </Typography>
                        <div className={classes.exploreButtonContainer}>
                          {renderRemainingQueries()}
                        </div>
                      </Box>
                    )}
                </div>
              </Box>

              {/* User message input area - bottom area */}
              <Box className={classes.chatInput}>
                <TextField
                  style={{ height: '100%' }}
                  fullWidth={true}
                  multiline={true}
                  maxRows={3}
                  placeholder="Poser une question à votre assistant IA"
                  value={userInput}
                  onChange={e => setUserInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        onClick={handleSendMessage}
                        disabled={!userInput.trim() || isSending}
                        style={{
                          backgroundColor: !userInput.trim() || isSending ? '#ccc' : '#5763FF',
                          color: 'white',
                          width: 48,
                          height: 48,
                        }}>
                        {isSending ? (
                          <CircularProgress size={24} style={{ color: 'white' }} />
                        ) : (
                          <Arrow />
                        )}
                      </IconButton>
                    ),
                    style: {
                      borderRadius: '8px',
                      backgroundColor: '#ffffff',
                      height: '100%',
                      border: 'none',
                    },
                    classes: {
                      input: classes.inputPlaceholder,
                      notchedOutline: classes.noBorder,
                      root: classes.multilineInput,
                    },
                  }}
                />
              </Box>
            </Box>
          </Grid>

          {/* Right side - Synthesis area */}
          <Grid item={true} xs={5} xl={4}>
            <Box className={classes.synthesisArea}>
              {/* Synthesis header */}
              <Box className={classes.synthesisHeader}>
                <div className={classes.synthesisHeaderTop}>
                  <Typography className={classes.synthesisTitle}>
                    Synthèse de la consultation
                  </Typography>
                  {consultationDetails?.synthesis ? (
                    <>
                      <Typography className={classes.synthesisStatus} style={{ color: '#0E1247' }}>
                        Créée le{' '}
                        {consultationDetails?.synthesisCreatedAt
                          ? formatDate(consultationDetails?.synthesisCreatedAt)
                          : ''}
                      </Typography>
                    </>
                  ) : (
                    <Typography className={classes.synthesisStatus}>
                      Aucune synthèse n&apos;a été créé
                    </Typography>
                  )}
                </div>
                {consultationDetails?.synthesis ? (
                  <>
                    <div className={classes.synthesisHeaderBottom}>
                      <Button
                        className={classes.closeButton}
                        style={{ width: '100%', fontSize: '12px' }}
                        startIcon={<EmailIcon />}
                        onClick={() => openEmailDrawer()}>
                        Envoyer la synthèse par mail
                      </Button>
                      <Button
                        className={classes.closeButton}
                        style={{ width: '100%', fontSize: '12px' }}
                        startIcon={<FileDownloadIcon />}
                        onClick={handleDownloadSynthesis}>
                        Télécharger en format PDF
                      </Button>
                    </div>
                  </>
                ) : null}
              </Box>

              {/* Synthesis editor */}
              <Box className={classes.synthesisEditor}>
                <div className={classes.quillWrapper}>
                  <RichEditor
                    key={consultationId}
                    value={notesContent}
                    handleChange={setNotesContent}
                    placeholder="Renseignez la description du texte"
                    whiteBg={true}
                    isMultiline={true}
                  />
                </div>
                <div className={classes.synthesisActions}>
                  {consultationDetails?.notes ? (
                    <>
                      <Button
                        className={classes.synthesizeOutlinedButton}
                        onClick={handleSynthesis}
                        disabled={isCreatingSynthesis}>
                        {isCreatingSynthesis ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                            <CircularProgress size={16} />
                            Synthèse en cours
                          </span>
                        ) : (
                          'Synthétiser cette consultation'
                        )}
                      </Button>
                      <Button
                        className={classes.synthesizeButton}
                        onClick={handleValidateSynthesis}
                      >
                        <CheckIcon style={{ fontSize: 16, marginRight: 8 }} />
                        Valider la synthèse
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        className={classes.synthesizeOutlinedButton}
                        onClick={handleSynthesis}>
                        {isCreatingSynthesis ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                            <CircularProgress size={16} />
                            Synthèse en cours
                          </span>
                        ) : (
                          'Synthétiser cette consultation'
                        )}
                      </Button>
                      <Button
                        color="primary"
                        disabled={isSending || !notesContent}
                        className={classes.synthesizeButton}
                        onClick={handleSaveNotes}>
                        Enregistrer
                      </Button>
                    </>
                  )}
                </div>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
      <DrawerRight
        openDrawer={openDrawer}
        callBack={() => setOpenDrawer(false)}
        titleDrawer="Envoyer la synthèse au bénéficiaire">
        <div style={{ display: 'flex', flexDirection: 'column', padding: '20px' }}>
          <div style={{ marginBottom: '20px' }}>
            <Typography style={{ marginBottom: '20px', color: '#666' }}>
              Une fois l&apos;objet et le corps de l&apos;email validés, la synthèse réalisée avec
              l&apos;assistant IA sera transmise au bénéficiaire au format PDF.
            </Typography>
          </div>

          <div
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: '50px',
              justifyContent: 'space-between',
              height: '100%',
            }}>
            <div className={classes.formContainer}>
              <div className={classes.formField}>


                <input
                  type="text"
                  placeholder="Objet de l'email"
                  className={classes.formInput}
                  value={emailSubject}
                  onChange={e => setEmailSubject(e.target.value)}
                />
              </div>

              <div className={classes.formField}>

                <textarea
                  className={classes.contextTextarea}
                  placeholder="Corps de l'email"
                  value={emailBody}
                  onChange={e => setEmailBody(e.target.value)}
                  rows={10}
                  style={{ minHeight: '200px' }}
                />
              </div>
            </div>
            <div className={classes.bottomContainer}>
              <div className={classes.buttonContainer}>
                <Button
                  className={`${classes.baseButton} ${classes.primaryButton}`}
                  onClick={() => {
                    handleSendEmail();
                  }}>
                  Envoyer la synthèse
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DrawerRight>
    </Layout>
  );
};

export default ConsultationPage;
